import { useEffect, useState, useCallback } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "juke_pwa_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* storage may be blocked */ }
    setVisible(false);
    setShowIosHint(false);
  }, []);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    // Re-offer after 14 days
    if (dismissed && Date.now() - dismissed < 14 * 24 * 60 * 60 * 1000) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setShowIosHint(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show custom hint after a short delay.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setShowIosHint(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  };

  if (visible) {
    return (
      <div
        data-testid="pwa-install-banner"
        role="dialog"
        className="fixed inset-x-0 bottom-4 z-[65] px-4 pointer-events-none"
      >
        <div className="pointer-events-auto max-w-md mx-auto rounded-2xl border border-[#C5A059]/40 bg-black/90 backdrop-blur-xl shadow-[0_12px_48px_rgba(0,0,0,0.6)] p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium">Instalirajte Juke Driver</p>
            <p className="text-xs text-zinc-400">Pristup vožnjama jednim dodirom.</p>
          </div>
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-xs font-semibold uppercase tracking-[0.15em] transition-colors"
          >
            Instaliraj
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="pwa-install-dismiss"
            className="p-1 text-zinc-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        role="dialog"
        className="fixed inset-x-0 bottom-4 z-[65] px-4 pointer-events-none"
      >
        <div className="pointer-events-auto max-w-md mx-auto rounded-2xl border border-[#C5A059]/40 bg-black/90 backdrop-blur-xl shadow-[0_12px_48px_rgba(0,0,0,0.6)] p-4 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center shrink-0">
            <Share className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium">Dodajte Juke Driver na početni zaslon</p>
            <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">
              Dodirnite <Share className="w-3 h-3 inline align-text-bottom text-[#C5A059]" /> Dijeli
              u Safariju, zatim <strong className="text-zinc-200">Dodaj na početni zaslon</strong>.
            </p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="pwa-ios-hint-dismiss"
            className="p-1 text-zinc-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
